import { defineStore } from 'pinia'
import { apiService } from '@/services/api'
import { useUserStore } from '@/stores/userStore'
import { formatDate, getCurrentJSTIsoString } from '@/components/logics/dateUtil'

export const useTodoStore = defineStore('todo', {
  state: () => ({
    todos: [],
    tableList: [],
    formData: null,
    formInit: {
      id: null,
      companyId: null,
      branchId: null,
      staffId: null,
      todoMasterId: null,
      todoJson: {},
      status: 'incomplete',
      memo: '',
    },
    selectedTodo: null,
    // 新しいデータ構造に対応するための追加状態
    todoMasters: [], // TodoMasterの完全なリスト
    // 会社名リスト（IDと名前のみの軽量なリスト）
    companyNames: [],
    // 支店名リスト（IDと名前のみの軽量なリスト）
    branchNames: [],
    // スタッフ名リスト（IDと名前のみの軽量なリスト）
    staffNames: [],
    // UI表示用のフラットアイテムリスト
    flatItems: [],
    // UI用選択オプション
    selectedOptions: {},
    // 削除判定フラグ
    isDeletedCompany: false,
    isDeletedBranch: false,
    isDeletedStaff: false,
  }),
  actions: {
    // テーブル表示用のリストを初期化
    initTableList() {
      this.tableList = [
        {
          label: 'No',
          key: 'rowNumber',
          data: (item, value) => value,
        },
        {
          label: '会社',
          key: 'companyName',
          data: (item, value) => value || '削除された会社',
        },
        {
          label: '事業場',
          key: 'branchName',
          data: (item, value) => value || '削除された事業場',
        },
        {
          label: '従業員',
          key: 'staffName',
          data: (item, value) => value || '削除された従業員',
        },
        {
          label: '種別',
          key: 'todoMasterName',
          data: (item, value) => value || '削除された種別',
        },
        {
          label: 'ステータス',
          key: 'status',
          data: (item, value) => (value === 'incomplete' ? '未完了' : '完了'),
          tag: {
            完了: 'completed',
            未完了: 'incomplete',
          },
        },
        {
          label: '完了日',
          key: 'completeAt',
          data: (item, value) => formatDate(value) || '-',
        },
        {
          label: '登録日',
          key: 'createdAt',
          data: (item, value) => formatDate(value) || '-',
        },
        {
          label: '最終更新日',
          key: 'updatedAt',
          data: (item, value) => formatDate(value) || '-',
        },
      ]
    },

    // フォームデータの初期化
    async setFormData(id) {
      // フラットデータをリセット
      this.flatItems = []
      this.selectedOptions = {}

      // 削除フラグ初期化
      this.isDeletedCompany = false
      this.isDeletedBranch = false
      this.isDeletedStaff = false

      if (id) {
        // 編集の場合
        const todo = await this.getTodo(id)
        if (todo) {
          this.formData = JSON.parse(JSON.stringify(todo))

          // 会社リスト取得
          await this.getCompanyNames()
          // 会社が削除されているか判定
          this.isDeletedCompany = todo.companyId === null

          // 会社IDに基づいて支店リストを取得
          if (this.formData.companyId) {
            await this.getBranchNamesByCompanyId(this.formData.companyId)
            // 事業場が削除されているか判定
            this.isDeletedBranch = todo.branchId === null
          }
          // 支店IDに基づいて従業員リストを取得
          if (this.formData.branchId) {
            await this.getStaffNamesByBranchId(this.formData.branchId)
            // 従業員が削除されているか判定
            this.isDeletedStaff = todo.staffId === null
          }
          // 拡張データの初期化
          this.initFormExtension()

          // フラットアイテムリストを作成
          this.createFlatItemsList()

          // 既存の選択状態を復元
          this.restoreSelectedOptions()

          return
        }
      }
      // 新規作成の場合
      this.clearFormData()

      // 拡張データの初期化（新規作成用）
      this.initFormExtension(true)

      // フラットアイテムリストを作成
      this.createFlatItemsList()
    },

    // Todo一覧を取得
    async getTodos() {
      try {
        const response = await apiService.get(`/api/todos`)
        // レスポンスの形式に合わせて調整
        this.todos = response.todos || []
        return true
      } catch (error) {
        return false
      }
    },

    // Todoの情報を取得
    async getTodo(id) {
      try {
        const response = await apiService.get(`/api/todos/${id}`)
        let todo = response.todo || null

        // todoJsonが文字列の場合はパースする
        if (todo && todo.todoJson && typeof todo.todoJson === 'string') {
          todo.todoJson = JSON.parse(todo.todoJson)
        }

        return todo
      } catch (error) {
        return false
      }
    },

    // Todo情報を保存（新規作成・更新）
    async saveTodo() {
      try {
        // 会社IDを取得
        const companyId = this.formData.companyId

        // フラットデータからtodoJsonを更新
        this.updateTodoJsonFromFlatData()

        // UIデータをAPIフォーマットに変換
        this.prepareFormDataForSaving()

        // Todo全体のステータスを更新
        this.updateTodoStatus()

        // todoJsonをJSON文字列に変換
        // オブジェクト参照の複製を作成
        const requestData = { ...this.formData }

        // todoJsonをJSON文字列に変換してtodo_jsonフィールドに設定
        if (requestData.todoJson && typeof requestData.todoJson !== 'string') {
          requestData.todo_json = JSON.stringify(requestData.todoJson)
          delete requestData.todoJson // todoJsonフィールドは削除
        }

        // 変換後のデータを送信
        const response = await apiService.post(`/api/todos`, requestData)
        await this.getTodos()
        return response.message || (this.formData.id ? 'Todoを更新しました' : 'Todoを登録しました')
      } catch (error) {
        return null
      }
    },

    // Todoを削除
    async deleteTodo() {
      try {
        const response = await apiService.delete(`/api/todos/${this.formData.id}`)
        await this.getTodos()
        return response.message || 'Todoを削除しました'
      } catch (error) {
        return null
      }
    },

    // TodoMasterのオプションを取得
    async getTodoMasters() {
      try {
        const response = await apiService.get(`/api/todo-masters`)

        // レスポンスの形式に合わせて調整
        let todoMasters = response.todoMasters || []

        // todoJsonが文字列の場合、パースして変換する
        todoMasters = todoMasters.map(master => {
          if (master.todoJson && typeof master.todoJson === 'string') {
            try {
              // todoJsonをパースしてオブジェクトに変換
              const parsedJson = JSON.parse(master.todoJson)

              // default_todoJsonとして設定（元のtodoJsonも保持）
              return {
                ...master,
                default_todoJson: parsedJson,
              }
            } catch (e) {
              // パース失敗時は元のデータを返す
              return master
            }
          }
          return master
        })

        // 完全なリストを保存
        this.todoMasters = todoMasters

        return true
      } catch (error) {
        return false
      }
    },

    // 会社名リストを取得
    async getCompanyNames() {
      try {
        const response = await apiService.get('/api/companies/names')
        this.companyNames = response.companies || []
        return this.companyNames
      } catch (error) {
        this.companyNames = []
        return []
      }
    },

    // 会社IDで支店名リストを取得
    async getBranchNamesByCompanyId(companyId) {
      try {
        const response = await apiService.get(`/api/companies/${companyId}/branch-names`)
        this.branchNames = response.branches || []
        return this.branchNames
      } catch (error) {
        this.branchNames = []
        return []
      }
    },

    // 会社IDでスタッフ名リストを取得
    async getStaffNamesByCompanyId(companyId) {
      try {
        const response = await apiService.get(`/api/companies/${companyId}/staff-names`)
        this.staffNames = response.staff || []
        return this.staffNames
      } catch (error) {
        this.staffNames = []
        return []
      }
    },

    // 支店IDでスタッフ名リストを取得
    async getStaffNamesByBranchId(branchId) {
      try {
        const response = await apiService.get(`/api/branches/${branchId}/staff-names`)
        this.staffNames = response.staff || []
        return this.staffNames
      } catch (error) {
        this.staffNames = []
        return []
      }
    },

    // IDでTodoを検索
    getTodoById(id) {
      return this.todos.find(todo => todo.id === Number(id))
    },

    // フォームデータをクリア
    clearFormData() {
      this.formData = JSON.parse(JSON.stringify(this.formInit))
      // 削除フラグもリセット
      this.isDeletedCompany = false
      this.isDeletedBranch = false
      this.isDeletedStaff = false
    },

    // ステータスオプション
    getStatusOptions() {
      return [
        { value: 'incomplete', label: '未完了' },
        { value: 'completed', label: '完了' },
      ]
    },

    // ==================== 新しいデータ構造対応のための拡張メソッド ====================

    // 拡張データの初期化
    initFormExtension(isNew = false) {
      if (!this.formData) return

      // flowTypeの設定（todoMasterNameに基づく）
      if (this.formData.todoMasterName) {
        this.formData.flowType = this.formData.todoMasterName.toLowerCase().replace(/\s+/g, '_')
      }

      // 選択状態の初期化
      this.formData.selections = this.extractSelections(this.formData.todoJson || [])

      // 最後に編集したフロー情報を初期化
      this.formData.lastEditFlow = ''

      // 新規作成の場合、todoJsonが空なら初期化する
      if (isNew && (!this.formData.todoJson || Object.keys(this.formData.todoJson).length === 0)) {
        this.initEmptyTodoJson()
      }
    },

    // 新規作成時の空のtodoJsonを初期化
    async initEmptyTodoJson() {
      if (!this.formData || !this.formData.todoMasterId) return

      // デフォルトのtodoJsonを取得
      const defaultJson = await this.getDefaultTodoJson(this.formData.todoMasterId)
      this.formData.todoJson = defaultJson || []

      // 選択状態も初期化
      this.formData.selections = this.extractSelections(this.formData.todoJson)
    },

    // TodoMaster変更時の処理
    async onTodoMasterChange(todoMasterId) {
      if (!todoMasterId || !this.formData) return

      // 現在のTodoMasterを取得
      const selectedMaster = this.todoMasters.find(m => m.id == todoMasterId)
      if (!selectedMaster) return

      // フォームデータを更新
      this.formData.todoMasterId = todoMasterId
      this.formData.todoMasterName = selectedMaster.name

      // デフォルトのtodoJsonを取得（TodoMasterから直接取得できるようになった）
      const defaultJson = await this.getDefaultTodoJson(todoMasterId)

      this.formData.todoJson = defaultJson || []

      // UI表示用データを初期化
      this.initFormExtension()

      // 選択肢をリセット
      this.selectedOptions = {}

      // フラットアイテムリストを再作成
      this.createFlatItemsList()
    },

    // TodoMaster IDからデフォルトのtodoJsonを取得
    async getDefaultTodoJson(todoMasterId) {
      if (!todoMasterId) return []

      try {
        // todoMastersからデフォルトJSONを取得
        const todoMaster = this.todoMasters.find(m => m.id == todoMasterId)
        if (todoMaster && todoMaster.default_todoJson) {
          // デフォルトJSONがある場合はそれを使用
          return JSON.parse(JSON.stringify(todoMaster.default_todoJson))
        }

        // 空の配列を返す
        return []
      } catch (error) {
        return []
      }
    },

    // 選択状態を抽出
    extractSelections(todoJson, parentPath = '', result = {}) {
      if (!todoJson || !Array.isArray(todoJson)) return result

      todoJson.forEach((item, index) => {
        const currentPath = parentPath ? `${parentPath}.${index}` : `${index}`

        // 選択肢があれば処理
        if (item.selection && item.selection.length) {
          // 現在選択されている選択肢のインデックスを特定
          let selectedOptionIndex = null

          // checkedAtが設定されている子を持つ選択肢のインデックスを探す
          for (let optIndex = 0; optIndex < item.selection.length; optIndex++) {
            const option = item.selection[optIndex]
            if (option.children && option.children.some(child => child.checkedAt)) {
              selectedOptionIndex = optIndex
              break
            }
          }

          // 選択状態を記録（インデックス）
          result[currentPath] = selectedOptionIndex

          // 各選択肢の子要素も再帰的に処理
          item.selection.forEach((option, optIndex) => {
            if (option.children && option.children.length) {
              this.extractSelections(option.children, `${currentPath}.selection.${optIndex}.children`, result)
            }
          })
        }
      })

      return result
    },

    // TodoJsonからフラット化したアイテムリストを作成
    createFlatItemsList() {
      this.flatItems = []

      if (!this.formData || !this.formData.todoJson) {
        return
      }

      // 再帰的にアイテムを平坦化する関数
      const flattenItems = (items, level = 0, parentId = null, selectionOptionIndex = null, parentPath = '') => {
        if (!items || !Array.isArray(items)) return

        items.forEach((item, index) => {
          const currentPath = parentPath ? `${parentPath}.${index}` : `${index}`

          // 基本項目を追加
          const newItem = {
            ...item,
            id: `item-${currentPath.replace(/\./g, '-')}`,
            level,
            parentId,
            selectionOptionIndex, // インデックスに変更
            isSelectionChild: !!parentId,
            isVisible: !parentId,
            path: currentPath,
          }

          this.flatItems.push(newItem)

          // この項目に選択肢があれば処理
          if (item.selection && item.selection.length > 0) {
            item.selection.forEach((option, optIndex) => {
              if (option.children && option.children.length > 0) {
                // 選択肢の子アイテムを平坦化して追加（レベルは +1）
                flattenItems(option.children, level + 1, newItem.id, optIndex, `${currentPath}.selection.${optIndex}.children`)
              }
            })
          }
        })
      }

      // 平坦化を開始
      flattenItems(this.formData.todoJson)
    },

    // 既存の選択状態を復元
    restoreSelectedOptions() {
      if (!this.formData || !this.formData.todoJson || !this.flatItems.length) return

      this.selectedOptions = {}

      // フラットアイテムで選択状態を初期化
      this.flatItems.forEach(item => {
        // 選択肢を持つアイテムのみを処理
        if (item.selection && item.selection.length > 0) {
          // チェック済みの子アイテムを持つ選択肢のインデックスを探す
          for (let optIndex = 0; optIndex < item.selection.length; optIndex++) {
            const option = item.selection[optIndex]
            if (option.children && option.children.some(child => child.checkedAt)) {
              // この選択肢のインデックスが選択されていることを記録
              this.selectedOptions[item.id] = optIndex

              // 関連する子アイテムを表示に
              this.updateChildVisibility(item.id, optIndex)
              break
            }
          }
        }
      })

      // ネストされた選択肢も処理するため再帰的にチェック
      this.processNestedSelections(this.formData.todoJson)

      // 可視性を更新
      this.updateAllItemsVisibility()
    },

    // ネストされた選択肢を処理するヘルパーメソッド
    processNestedSelections(items, parentPath = '') {
      if (!items || !Array.isArray(items)) return

      items.forEach((item, index) => {
        const currentPath = parentPath ? `${parentPath}.${index}` : `${index}`

        // 選択肢があれば処理
        if (item.selection && item.selection.length) {
          // 選択されている選択肢のインデックスを特定
          let selectedOptionIndex = null

          for (let optIndex = 0; optIndex < item.selection.length; optIndex++) {
            const option = item.selection[optIndex]
            if (option.children && option.children.some(child => child.checkedAt)) {
              selectedOptionIndex = optIndex

              // この選択肢に対応するフラットアイテムを探す
              const flatItem = this.flatItems.find(fi => fi.path === currentPath)
              if (flatItem) {
                this.selectedOptions[flatItem.id] = selectedOptionIndex
                this.updateChildVisibility(flatItem.id, selectedOptionIndex)
              }

              // 選択肢の子アイテム内のネストされた選択肢も処理
              option.children.forEach((child, childIndex) => {
                if (child.selection && child.selection.length) {
                  this.processNestedSelections([child], `${currentPath}.selection.${optIndex}.children.${childIndex}`)
                }
              })

              break
            }
          }
        }
      })
    },

    // アイテムのチェック状態を更新
    updateItemChecked(item) {
      if (!item) return

      const userStore = useUserStore()

      // チェック状態を反転
      if (!item.checkedAt) {
        item.checkedAt = getCurrentJSTIsoString() // 案件JSONはそのまま保存して表示するため、この画面だけJSTに直して送信する
        item.checkedUserName = userStore.currentUserShortName
        item.checkedUserId = userStore.currentUserId
      } else {
        item.checkedAt = ''
        item.checkedUserName = ''
        item.checkedUserId = null
      }
    },

    // アイテムのメモを更新
    updateItemNoteFromUI(item) {
      if (!item) return
    },

    // 選択肢変更前に確認
    confirmSelectionChange(parentItem, newOptionIndex) {
      // 現在の選択肢インデックス
      const currentOptionIndex = this.selectedOptions[parentItem.id]

      // 選択肢が変更されていなければ確認不要
      if (currentOptionIndex === newOptionIndex) {
        return true
      }

      // 現在の選択肢に関連する子アイテムのチェック状態や、子アイテムの選択状態を確認
      const hasCheckedItems = this.flatItems.some(item => item.isSelectionChild && item.parentId === parentItem.id && item.selectionOptionIndex === currentOptionIndex && item.isVisible && (item.checkedAt || this.selectedOptions[item.id]))

      // チェック済みの項目や選択肢選択がある場合は確認が必要
      return !hasCheckedItems
    },

    // 選択肢を選択
    selectOption(parentItem, optionIndex) {
      if (!parentItem || !parentItem.selection) return

      // まずすべての子アイテムを非表示に
      this.hideAllChildItems(parentItem.id)

      // 選択肢インデックスを保存
      this.selectedOptions[parentItem.id] = optionIndex

      // 関連する子アイテムの表示状態を更新
      this.updateChildVisibility(parentItem.id, optionIndex)

      // 選択肢変更時にチェック状態と選択状態をリセット
      this.resetChildrenStates(parentItem.id)
    },

    // 親アイテムに紐づくすべての子アイテムを非表示
    hideAllChildItems(parentId) {
      this.flatItems.forEach(item => {
        if (item.isSelectionChild && item.parentId === parentId) {
          item.isVisible = false
        }
      })
    },

    // 子アイテムの表示状態を更新
    updateChildVisibility(parentId, selectedOptionIndex) {
      // 選択肢インデックスに一致する直接の子アイテムを表示
      this.flatItems.forEach(item => {
        if (item.isSelectionChild && item.parentId === parentId && item.selectionOptionIndex === selectedOptionIndex) {
          item.isVisible = true

          // さらに、この子アイテムが選択状態を持っていれば、その選択状態も復元
          if (item.selection && item.selection.length && this.selectedOptions[item.id] !== undefined) {
            this.updateChildVisibility(item.id, this.selectedOptions[item.id])
          }
        }
      })
    },

    // すべてのアイテムの可視性を更新（ネストされた選択肢に対応）
    updateAllItemsVisibility() {
      // 基本アイテム（非選択肢の子）は常に表示
      this.flatItems.forEach(item => {
        if (!item.isSelectionChild) {
          item.isVisible = true
        } else {
          // 選択肢の子アイテムは条件付きで表示
          const parentItem = this.flatItems.find(fi => fi.id === item.parentId)
          if (parentItem && this.selectedOptions[parentItem.id] === item.selectionOptionIndex) {
            item.isVisible = true

            // この子アイテムが選択肢を持ち、その選択肢が選択されている場合、その子も表示
            if (item.selection && item.selection.length && this.selectedOptions[item.id] !== undefined) {
              this.updateChildVisibility(item.id, this.selectedOptions[item.id])
            }
          }
        }
      })
    },

    // 選択肢変更時に子アイテムのチェック状態と選択状態をリセット
    resetChildrenStates(parentId) {
      // 選択肢の子アイテムとその子孫をすべてリセット
      const childrenToReset = this.flatItems.filter(item => item.isSelectionChild && item.parentId === parentId)

      // リセット対象のID一覧を取得
      const resetIds = childrenToReset.map(item => item.id)

      // チェック状態をリセット
      childrenToReset.forEach(item => {
        // チェック状態をリセット
        item.checkedAt = ''
        item.checkedUserName = ''

        // 選択状態もリセット（子アイテムが選択肢を持つ場合）
        if (item.id in this.selectedOptions) {
          delete this.selectedOptions[item.id]
        }
      })

      // 子孫の選択肢も再帰的にリセット
      const findChildren = parentIds => {
        const children = this.flatItems.filter(item => item.isSelectionChild && parentIds.includes(item.parentId))

        if (children.length === 0) return

        const childIds = children.map(item => item.id)

        // チェック状態をリセット
        children.forEach(item => {
          item.checkedAt = ''
          item.checkedUserName = ''

          // 選択状態もリセット
          if (item.id in this.selectedOptions) {
            delete this.selectedOptions[item.id]
          }

          // 非表示に
          item.isVisible = false
        })

        // さらにその子を再帰的に処理
        findChildren(childIds)
      }

      findChildren(resetIds)
    },

    // アイテムが表示されるべきかを判定
    shouldShowItem(item) {
      if (!item) return false

      if (!item.isSelectionChild) {
        // 基本項目は常に表示
        return true
      }

      // 選択肢の子アイテムの場合、可視性フラグで判定
      return item.isVisible
    },

    // 保存前にフラットデータからtodoJsonを更新
    updateTodoJsonFromFlatData() {
      if (!this.formData || !this.formData.todoJson || !this.flatItems.length) {
        return
      }

      // selectionsの更新
      this.formData.selections = { ...this.selectedOptions }

      // チェック状態とメモの更新
      this.flatItems.forEach(item => {
        if (!item.path) return

        const todoItem = this.getTodoJsonItemByPath(item.path)
        if (todoItem) {
          // チェック状態を更新
          todoItem.checkedAt = item.checkedAt || ''
          todoItem.checkedUserName = item.checkedUserName || ''
          todoItem.note = item.note || ''
          todoItem.checkedUserId = item.checkedUserId || null // ユーザーIDも更新
        }
      })
    },

    // 保存前にフォームデータを準備
    prepareFormDataForSaving() {
      if (!this.formData) return

      // 選択状態とチェック状態をtodoJsonに反映
      this.updateTodoJsonFromUiState()

      // 不要なUI専用プロパティを削除
      const saveData = JSON.parse(JSON.stringify(this.formData))

      // UI用の一時的なプロパティを削除
      delete saveData.flowType
      delete saveData.selections
      delete saveData.lastEditFlow

      // todoJsonの各項目からUI専用プロパティを削除（再帰的に処理）
      if (saveData.todoJson && Array.isArray(saveData.todoJson)) {
        this.cleanupTodoJsonForApi(saveData.todoJson)
      }

      this.formData = saveData
    },

    // todoJsonからUI専用のプロパティを削除（APIに送信する前の整形）
    cleanupTodoJsonForApi(items) {
      if (!items || !Array.isArray(items)) return

      for (const item of items) {
        // UI専用プロパティの削除
        delete item.isVisible
        delete item.isSelectionChild
        delete item.parentId
        delete item.id // UI用の一意のID
        delete item.level
        delete item.path

        // 選択肢がある場合、再帰的に処理
        if (item.selection && Array.isArray(item.selection)) {
          for (const option of item.selection) {
            if (option.children && Array.isArray(option.children)) {
              this.cleanupTodoJsonForApi(option.children)
            }
          }
        }
      }
    },

    // UI状態をtodoJsonに反映
    updateTodoJsonFromUiState() {
      if (!this.formData || !this.formData.todoJson || !this.formData.selections) return

      // 選択状態の反映
      this.updateSelectionsInTodoJson()
    },

    // 選択状態をtodoJsonに反映
    updateSelectionsInTodoJson() {
      if (!this.formData || !this.formData.todoJson || !this.formData.selections) return

      const userStore = useUserStore()

      // 各選択状態を元のtodoJsonに反映
      Object.entries(this.formData.selections).forEach(([path, selectedOption]) => {
        if (!selectedOption) return

        const pathParts = path.split('.')
        let current = this.formData.todoJson
        let target = null

        // パスを辿って対象要素を特定
        for (let i = 0; i < pathParts.length; i++) {
          const part = pathParts[i]
          if (part === 'selection' || part === 'children') {
            current = current[part]
          } else {
            const index = parseInt(part, 10)
            if (i === pathParts.length - 1) {
              target = current[index]
            } else {
              current = current[index]
            }
          }

          if (!current) return
        }

        // 対象要素の選択肢を更新
        if (target && target.selection) {
          // 非選択状態にする（初期化）
          target.selection.forEach(option => {
            if (option.children) {
              option.children.forEach(child => {
                child.checkedAt = ''
                child.checkedUserName = ''
              })
            }
          })

          // 選択された選択肢の子要素を選択状態にする
          const selectedOpt = target.selection.find(opt => opt.option === selectedOption)
          if (selectedOpt && selectedOpt.children) {
            // 選択された選択肢の最初の子要素にチェックを入れる
            if (selectedOpt.children.length > 0) {
              selectedOpt.children[0].checkedAt = getCurrentJSTIsoString()
              selectedOpt.children[0].checkedUserName = userStore.currentUserShortName
            }
          }
        }
      })
    },

    // アイテムのメモを更新
    updateItemNote(itemPath, noteText) {
      if (!itemPath || !this.formData || !this.formData.todoJson) return

      const item = this.getTodoJsonItemByPath(itemPath)
      if (!item) return

      // メモを更新
      item.note = noteText
    },

    // パスに基づいてtodoJson内の項目を取得
    getTodoJsonItemByPath(path) {
      if (!path || !this.formData || !this.formData.todoJson) return null

      const pathParts = path.split('.')
      let current = this.formData.todoJson

      for (const part of pathParts) {
        if (part === 'selection' || part === 'children') {
          current = current[part]
        } else {
          const index = parseInt(part, 10)
          current = current[index]
        }

        if (!current) return null
      }

      return current
    },

    // 項目が表示されるべきかどうかを判定
    isItemVisible(itemPath) {
      if (!itemPath || !this.formData || !this.formData.todoJson || !this.formData.selections) return false

      const pathParts = itemPath.split('.')
      let currentPath = ''
      let isVisible = true

      for (let i = 0; i < pathParts.length; i++) {
        const part = pathParts[i]

        if (part === 'selection') {
          // selectionの場合は親項目の選択状態を確認
          const parentPath = currentPath
          const selectedOption = this.formData.selections[parentPath]

          // 選択されていない場合は表示しない
          if (!selectedOption) {
            isVisible = false
            break
          }

          // 次のインデックスを確認（選択肢のインデックス）
          if (i + 1 < pathParts.length) {
            const optionIndex = parseInt(pathParts[i + 1], 10)
            const parentItem = this.getTodoJsonItemByPath(parentPath)

            if (parentItem && parentItem.selection && parentItem.selection[optionIndex]) {
              // この選択肢が選択されているか確認
              if (parentItem.selection[optionIndex].option !== selectedOption) {
                isVisible = false
                break
              }
            }
          }
        } else if (part !== 'children') {
          // 通常のインデックスまたはchildrenの場合はパスを更新
          if (currentPath) {
            currentPath += '.' + part
          } else {
            currentPath = part
          }
        }
      }

      return isVisible
    },

    // Todo全体の完了状態を更新
    updateTodoStatus() {
      if (!this.formData) return

      const isComplete = this.isFlowComplete()

      // ステータスと完了日を更新
      this.formData.status = isComplete ? 'complete' : 'incomplete'
      this.formData.complete_at = isComplete ? getCurrentJSTIsoString() : null
    },

    // フロー内に未選択の選択肢があるかをチェック
    hasUnselectedOptions() {
      if (!this.flatItems) return true

      // 表示されている選択肢を持つ項目を対象に
      const itemsWithSelections = this.flatItems.filter(item => this.shouldShowItem(item) && item.selection && item.selection.length > 0)

      // 修正：0も有効な選択として扱う
      const result = itemsWithSelections.some(item => this.selectedOptions[item.id] === undefined || this.selectedOptions[item.id] === null)

      return result
    },

    // フローが完了しているかを判定
    isFlowComplete() {
      if (!this.formData || !this.formData.todoJson) return false

      // 1. 表示されているフラットアイテムでチェック
      const visibleItems = this.flatItems.filter(item => this.shouldShowItem(item))
      const allChecked = visibleItems.length > 0 && visibleItems.every(item => !!item.checkedAt)

      // 2. 未選択の選択肢がないことを確認
      const allOptionsSelected = !this.hasUnselectedOptions()

      // 両方の条件を満たす場合のみ完了
      return allChecked && allOptionsSelected
    },

    // Todo項目の一覧を平坦化して取得
    getAllTodoItems() {
      if (!this.formData || !this.formData.todoJson) return []

      const result = []

      // 再帰的に項目を収集
      const collectItems = (items, parentPath = '') => {
        if (!items || !Array.isArray(items)) return

        items.forEach((item, index) => {
          const currentPath = parentPath ? `${parentPath}.${index}` : `${index}`

          // 基本情報を追加
          result.push({
            path: currentPath,
            label: item.label,
            tip: item.tip || '',
            note: item.note || '',
            checkedAt: item.checkedAt || '',
            checkedUserName: item.checkedUserName || '',
            hasFork: !!(item.selection && item.selection.length),
            done: !!item.checkedAt,
          })

          // 選択肢があれば処理
          if (item.selection && item.selection.length) {
            item.selection.forEach((option, optIndex) => {
              // 選択肢の子要素を処理
              if (option.children && option.children.length) {
                collectItems(option.children, `${currentPath}.selection.${optIndex}.children`)
              }
            })
          }
        })
      }

      collectItems(this.formData.todoJson)

      return result
    },

    // コピー時の処理
    onCopy() {
      if (!this.formData) return false

      const userStore = useUserStore()

      // IDをnullにして新規として扱う
      this.formData.id = null

      // チェック情報を更新する関数
      const updateCheckInfo = items => {
        if (!items || !Array.isArray(items)) return

        const currentDate = getCurrentJSTIsoString()
        const currentUser = userStore.currentUserShortName
        const currentUserId = userStore.currentUserId

        items.forEach(item => {
          // チェック済みの項目の場合、日付とユーザー名を更新
          if (item.checkedAt) {
            item.checkedAt = currentDate
            item.checkedUserName = currentUser
            item.checkedUserId = currentUserId
          }

          // 選択肢の子アイテムも再帰的に処理
          if (item.selection && Array.isArray(item.selection)) {
            item.selection.forEach(option => {
              if (option.children && Array.isArray(option.children)) {
                updateCheckInfo(option.children)
              }
            })
          }
        })
      }

      // todoJsonのチェック情報を更新
      if (this.formData.todoJson && Array.isArray(this.formData.todoJson)) {
        updateCheckInfo(this.formData.todoJson)
      }

      // フラットアイテムリストも更新
      this.flatItems.forEach(item => {
        if (item.checkedAt) {
          item.checkedAt = getCurrentJSTIsoString()
          item.checkedUserName = userStore.currentUserShortName
          item.checkedUserId = userStore.currentUserId
        }
      })

      return true
    },

    // 会社変更時の処理
    async onCompanyChange() {
      if (!this.formData) return

      // 事業場と従業員をリセット
      this.formData.branchId = null
      this.formData.staffId = null
      // 削除フラグをリセット
      this.isDeletedCompany = false
      this.isDeletedBranch = false
      this.isDeletedStaff = false

      // 事業場オプションを取得
      await this.getBranchNamesByCompanyId(this.formData.companyId)
      this.staffNames = []
    },

    // 事業場変更時の処理
    async onBranchChange() {
      if (!this.formData) return

      // 従業員をリセット
      this.formData.staffId = null
      // 削除フラグをリセット
      this.isDeletedBranch = false
      this.isDeletedStaff = false

      // 従業員オプションを取得
      await this.getStaffNamesByBranchId(this.formData.branchId)
    },

    // 会社セレクトの状態を返す
    getCompanySelectState(isEdit = false) {
      const formData = this.formData
      if (!formData) return null
      const companyId = formData.companyId
      if (isEdit && this.isDeletedCompany) return 'deleted'
      if (companyId === null || companyId === -1) return 'select'
      return null
    },

    // 事業場セレクトの状態を返す
    getBranchSelectState(isEdit = false) {
      const formData = this.formData
      if (!formData) return 'select'
      const branchId = formData.branchId
      const companyId = formData.companyId
      if (!companyId || companyId === -1) return null
      if (isEdit && this.isDeletedBranch) return 'deleted'
      if ((branchId === null || branchId === -1) && companyId) return 'select'
      return null
    },

    // 従業員セレクトの状態を返す
    getStaffSelectState(isEdit = false) {
      const formData = this.formData
      if (!formData) return 'select'

      const staffId = formData.staffId
      const branchId = formData.branchId

      // 事業場が選択されていない場合、従業員も選択できない
      if (!branchId || branchId === -1) {
        return null // disabled状態
      }

      // 編集モードで、削除された従業員フラグがtrueの場合
      if (isEdit && this.isDeletedStaff) {
        return 'deleted'
      }

      // staffIdがnullで事業場が選択されている場合は「選択」状態
      if ((staffId === null || staffId === -1) && branchId) {
        return 'select'
      }

      return null // 正常に選択されている状態
    },

    // フローに変更があるかどうかを判定
    hasFlowChanges() {
      if (!this.formData || !this.flatItems || this.flatItems.length === 0) {
        return false
      }

      // チェック状態に変更があるかを確認
      const hasCheckedChanges = this.flatItems.some(item => !!item.checkedAt)

      // メモに変更があるかを確認
      const hasMemoChanges = this.flatItems.some(item => item.note && item.note.trim() !== '')

      // 選択状態に変更があるかを確認
      const hasSelectionChanges = Object.keys(this.selectedOptions).length > 0

      return hasCheckedChanges || hasMemoChanges || hasSelectionChanges
    },
  },
  getters: {
    // フォーマットされたTodoJsonプレビュー
    formattedTodoJson() {
      if (!this.formData || !this.formData.todoJson) return ''
      try {
        return JSON.stringify(this.formData.todoJson, null, 2)
      } catch (e) {
        return ''
      }
    },

    // 表示すべき項目のみをフィルタリング
    visibleItems() {
      if (!this.formData || !this.formData.todoJson) return []

      return this.getAllTodoItems().filter(item => this.isItemVisible(item.path))
    },
    // TodoMaster名リストからセレクトボックス用の選択肢を生成
    todoOptions() {
      return this.todoMasters.map(master => ({
        label: master.name,
        value: master.id,
      }))
    },

    // 会社名リストからセレクトボックス用の選択肢を生成
    companyNameOptions(state) {
      return state.companyNames.map(company => ({
        label: company.name,
        value: company.id,
      }))
    },

    // 事業場名リストからセレクトボックス用の選択肢を生成
    branchNameOptions(state) {
      return state.branchNames.map(branch => ({
        label: branch.name,
        value: branch.id,
      }))
    },

    // スタッフ名リストからセレクトボックス用の選択肢を生成
    staffNameOptions(state) {
      return state.staffNames.map(staff => ({
        label: staff.name,
        value: staff.id,
      }))
    },
    // 会社が選択済みかどうかを確認
    isCompanySelected(state) {
      return !!state.formData?.companyId && state.formData.companyId !== -1
    },
  },
})
